import { runTest, assert, delay, TestResult } from '../testUtils';
import * as indexedDBService from '../../utils/indexedDBService';

/**
 * Test suite for client functionality
 */
export const runClientTests = async (): Promise<TestResult[]> => {
  const results: TestResult[] = [];

  // Test 1: Client List Loading
  results.push(await runTest(
    'Client List Loading',
    async () => {
      try {
        // Get clients from IndexedDB
        const clients = await indexedDBService.getAllItems<any>('clients');

        // Verify we can read client data
        return {
          success: true,
          message: `Successfully loaded ${clients.length} clients from database`
        };
      } catch (error) {
        return {
          success: false,
          message: 'Error loading client list',
          details: error instanceof Error ? error.message : String(error)
        };
      }
    }
  ));

  // Test 2: Client GSTIN Validation
  results.push(await runTest(
    'Client GSTIN Validation',
    async () => {
      try {
        // Test valid and invalid GSTIN formats
        const validGSTINs = [
          '29AABCA1234D1Z5',
          '27AADCS1234A1Z9',
          '06AABCD1234E1ZP'
        ];

        const invalidGSTINs = [
          '29AABCA1234D1Z', // Too short
          '29AABCA1234D1Z55', // Too long
          'AAAABCA1234D1Z5', // Invalid state code
          '29AABCA1234D1ZX'  // Invalid check digit
        ];

        // Simple GSTIN validation function
        const isValidGSTIN = (gstin: string): boolean => {
          // Basic format check: 2 digits state code + 10 chars PAN + 1 char entity + 1 char Z + 1 char check digit
          const gstinRegex = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
          return gstinRegex.test(gstin);
        };

        // Check valid GSTINs
        for (const gstin of validGSTINs) {
          assert(isValidGSTIN(gstin)).toBe(true);
        }

        // Check invalid GSTINs
        for (const gstin of invalidGSTINs) {
          assert(isValidGSTIN(gstin)).toBe(false);
        }

        return {
          success: true,
          message: 'GSTIN validation works correctly for both valid and invalid formats'
        };
      } catch (error) {
        return {
          success: false,
          message: 'Error testing GSTIN validation',
          details: error instanceof Error ? error.message : String(error)
        };
      }
    }
  ));

  // Test 3: Client State Code from GSTIN
  results.push(await runTest(
    'Extract Client State Code from GSTIN',
    async () => {
      try {
        // Test cases: GSTIN and expected state
        const testCases = [
          { gstin: '29AABCA1234D1Z5', state: 'Karnataka' },
          { gstin: '27AADCS1234A1Z9', state: 'Maharashtra' },
          { gstin: '06AABCD1234E1ZP', state: 'Haryana' }
        ];

        // State code mapping
        const stateCodeMap: Record<string, string> = {
          '01': 'Jammu and Kashmir',
          '02': 'Himachal Pradesh',
          '03': 'Punjab',
          '04': 'Chandigarh',
          '05': 'Uttarakhand',
          '06': 'Haryana',
          '07': 'Delhi',
          '08': 'Rajasthan',
          '09': 'Uttar Pradesh',
          '10': 'Bihar',
          '11': 'Sikkim',
          '12': 'Arunachal Pradesh',
          '13': 'Nagaland',
          '14': 'Manipur',
          '15': 'Mizoram',
          '16': 'Tripura',
          '17': 'Meghalaya',
          '18': 'Assam',
          '19': 'West Bengal',
          '20': 'Jharkhand',
          '21': 'Odisha',
          '22': 'Chhattisgarh',
          '23': 'Madhya Pradesh',
          '24': 'Gujarat',
          '26': 'Dadra and Nagar Haveli and Daman and Diu',
          '27': 'Maharashtra',
          '28': 'Andhra Pradesh',
          '29': 'Karnataka',
          '30': 'Goa',
          '31': 'Lakshadweep',
          '32': 'Kerala',
          '33': 'Tamil Nadu',
          '34': 'Puducherry',
          '35': 'Andaman and Nicobar Islands',
          '36': 'Telangana',
          '37': 'Andhra Pradesh (New)',
          '38': 'Ladakh'
        };

        // Function to extract state code from GSTIN
        const getStateFromGSTIN = (gstin: string): string | null => {
          if (!gstin || gstin.length < 2) return null;
          const stateCode = gstin.substring(0, 2);
          return stateCodeMap[stateCode] || null;
        };

        // Test each case
        for (const { gstin, state } of testCases) {
          const extractedState = getStateFromGSTIN(gstin);
          assert(extractedState).toBe(state);
        }

        return {
          success: true,
          message: 'State code extraction from GSTIN works correctly'
        };
      } catch (error) {
        return {
          success: false,
          message: 'Error testing state code extraction',
          details: error instanceof Error ? error.message : String(error)
        };
      }
    }
  ));

  // Test 4: Check Client Field Requirements
  results.push(await runTest(
    'Client Required Fields Check',
    async () => {
      try {
        // Simulate form validation for client
        const requiredFields = ['name', 'address', 'state'];

        // Test with valid client
        const validClient = {
          name: 'Test Client',
          address: '123 Main St',
          city: 'Bangalore',
          state: 'Karnataka',
          pincode: '560001',
          gstin: '29AABCA1234D1Z5',
          phone: '9876543210',
          email: 'test@example.com'
        };

        // Check required fields in valid client
        const missingFieldsValid = requiredFields.filter(field => !validClient[field as keyof typeof validClient]);
        assert(missingFieldsValid.length).toBe(0);

        // Test with invalid client (missing fields)
        const invalidClient = {
          name: 'Test Client',
          city: 'Bangalore',
          pincode: '560001',
          email: 'test@example.com'
        };

        // Check required fields in invalid client
        const missingFieldsInvalid = requiredFields.filter(field => !invalidClient[field as keyof typeof invalidClient]);
        assert(missingFieldsInvalid.length).toBeGreaterThan(0);
        assert(missingFieldsInvalid).toContain('address');
        assert(missingFieldsInvalid).toContain('state');

        return {
          success: true,
          message: 'Client field validation works correctly'
        };
      } catch (error) {
        return {
          success: false,
          message: 'Error testing client field validation',
          details: error instanceof Error ? error.message : String(error)
        };
      }
    }
  ));

  // Test 5: Email Validation for Clients
  results.push(await runTest(
    'Client Email Validation',
    async () => {
      try {
        // Test valid and invalid email formats
        const validEmails = [
          'test@example.com',
          'user.name@domain.co.in',
          'user-name@domain.com',
          'user123@domain.org'
        ];

        const invalidEmails = [
          'invalid-email',
          'user@',
          '@domain.com',
          'user@domain'
        ];

        // Simple email validation function
        const isValidEmail = (email: string): boolean => {
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          return emailRegex.test(email);
        };

        // Check valid emails
        for (const email of validEmails) {
          assert(isValidEmail(email)).toBe(true);
        }

        // Check invalid emails
        for (const email of invalidEmails) {
          assert(isValidEmail(email)).toBe(false);
        }

        return {
          success: true,
          message: 'Email validation works correctly'
        };
      } catch (error) {
        return {
          success: false,
          message: 'Error testing email validation',
          details: error instanceof Error ? error.message : String(error)
        };
      }
    }
  ));

  return results;
}; 
